import { Divider } from '@design-system';
import { FC } from 'react';
import SbEditable from 'storyblok-react';

import { StaticPage } from '../../types/StaticContentType';
import { AnalyticsEmitter } from '../../utils/analytics/emitter';
import { ArtistsGroup } from '../ArtistsGroup/ArtistsGroup';
import { StoryblokGrid } from '../Grid/StoryblokGrid';
import { ImageGroup, ImageLinkGroup } from '../ImageCard';
import PageSection from '../Layout/PageSection/PageSection';
import { LogosGroup } from '../LogosGroup/LogosGroup';
import { QuoteGroup } from '../QuoteGroup/QuoteGroup';
import { SectionFAQs } from '../SectionFAQs/SectionFAQs';
import { TextGroup } from '../TextGroup/TextGroup';
import { StaticText } from './StaticText';

type Sections = StaticPage['content']['sections'];

interface Props {
  sections: Sections;
  track: AnalyticsEmitter;
}

export const StaticSections: FC<Props> = ({ sections, track }) => {
  const component = (blok: Sections[0]) => {
    switch (blok.component) {
      case 'artistsGroup':
        return <ArtistsGroup {...(blok as ArtistsGroupBlok)} />;
      case 'divider':
        return (
          <PageSection>
            <Divider className="my-9 md:my-12" />
          </PageSection>
        );
      case 'grid':
        return (
          <PageSection fullWidth>
            <StoryblokGrid
              onLinkClick={(href, title) =>
                track('link card', 'click', { href, title })
              }
              {...(blok as GridBlok)}
            />
          </PageSection>
        );
      case 'imageGroup':
        return (
          <PageSection fullWidth>
            <ImageGroup {...(blok as ImageGroupBlok)} />
          </PageSection>
        );
      case 'imageLinkGroup':
        return (
          <PageSection fullWidth>
            <ImageLinkGroup
              onLinkClick={(href, title) =>
                track('image link card', 'click', { href, title })
              }
              {...(blok as ImageLinkGroupBlok)}
            />
          </PageSection>
        );
      case 'logosGroup':
        return (
          <PageSection>
            <LogosGroup {...(blok as LogosGroupBlok)} />
          </PageSection>
        );
      case 'quoteGroup':
        return (
          <PageSection>
            <QuoteGroup {...(blok as QuoteGroupBlok)} />
          </PageSection>
        );
      case 'sectionFaqs':
        return (
          <PageSection>
            <SectionFAQs
              onQuestionClick={(title) =>
                track('faq item', 'expand', { title })
              }
              {...(blok as StoryblokSectionFAQs)}
            />
          </PageSection>
        );
      case 'richText':
        return <StaticText {...(blok as RichTextBlok)} />;
      case 'textGroup':
        return (
          <PageSection>
            <TextGroup {...(blok as TextGroupBlok)} />
          </PageSection>
        );
      default:
        return null;
    }
  };

  return (
    <article className="space-y-9 md:space-y-12">
      {sections.map((blok) => (
        // @ts-expect-error require storyblok-react upgrade
        <SbEditable key={blok._uid} content={blok}>
          {component(blok)}
        </SbEditable>
      ))}
    </article>
  );
};
